"use client";

import React, { useEffect, useState, useRef } from "react";
import { Modal, Button, Input, Select, Spin, Space, Popconfirm } from "antd";
import { ArrowLeftOutlined, CloseOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import { useAuth } from "../../services/authentication_API/authService";
import { getFees } from "../../services/doctor/fees/fees";
import { addBill, updateBill } from "../../services/doctor/bills/bills";
import { useMessageApi } from "../../services/message api/MessageProvider";

const ItemizedBillModal = ({
  isOpen,
  onClose,
  patientData,
  refresh,
  isedit,
  editBillData,
}) => {
  const [billItems, setBillItems] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("CASH");
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [getFeesData, setGetFeesData] = useState([]);
  const [errors, setErrors] = useState({});
  const [billData, setBillData] = useState(editBillData);

  const { userData } = useAuth();
  const messageApi = useMessageApi();
  const printRef = useRef(null);

  const totalBillAmount = billItems.reduce(
    (sum, item) => sum + Number.parseFloat(item.fee_amount || 0),
    0,
  );

  const validateForm = () => {
    let newErrors = {};
    if (billItems.length === 0) {
      newErrors.bill_item = "Please select at least one bill item";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // --- UPDATED PRINT LOGIC WITH EXACT STYLES ---
  const handlePrint = (data) => {
    const printContents = printRef.current.innerHTML;
    const printWindow = window.open("", "PRINT", "height=800,width=1000");

    printWindow.document.write(`
      <html>
        <head>
          <title>Bill Receipt</title>
          <style>
            body { font-family: 'Segoe UI', Arial, sans-serif; padding: 20px; color: #333; }
            .header { text-align: center; margin-bottom: 20px; }
            .clinic-name { font-size: 24px; font-weight: 500; letter-spacing: 1px; margin-bottom: 5px; text-transform: uppercase; }
            .clinic-details { font-size: 12px; margin: 2px 0; color: #444; }
            
            .info-section { border-top: 1px solid #000; border-bottom: 1px solid #000; padding: 10px 0; margin: 15px 0; }
            .info-row { display: flex; justify-content: space-between; margin-bottom: 4px; }
            .info-col { display: flex; width: 48%; font-size: 13px; }
            .label { font-weight: bold; width: 110px; }
            
            .bill-meta { font-size: 13px; margin: 10px 0; }
            
            table { width: 100%; border-collapse: collapse; font-size: 13px; margin-bottom: 5px; }
            th { background-color: #e0e0e0 !important; -webkit-print-color-adjust: exact; text-align: left; padding: 8px; font-weight: bold; }
            td { padding: 8px; border-bottom: 1px solid #eee; }
            .text-right { text-align: right; }
            
            .total-row { text-align: right; font-weight: bold; padding: 10px 0; font-size: 13px; border-bottom: 1px solid #eee; }
            .footer-summary { display: flex; justify-content: space-between; margin-top: 15px; font-weight: bold; font-size: 13px; }
            
            @media print { 
               body { padding: 0; }
               th { background-color: #e0e0e0 !important; -webkit-print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          ${printContents}
          <script>
            window.onload = function() {
              window.focus();
              window.print();
              window.close();
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleSubmit = async (shouldPrint = false) => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        patient_id: patientData?.id,
        doctor_id: userData?.id,
        payment_mode: paymentMethod,
        items: billItems,
      };

      const result = isedit
        ? await updateBill(editBillData.id, payload)
        : await addBill(payload);

      if (result.success) {
        const responseData = result?.data;
        setBillData(responseData);

        if (shouldPrint) {
          // Allow state to update so Bill No/Receipt No populate in the ref
          setTimeout(async () => {
            const printHtml = printRef.current.innerHTML;
            const updateHtmlPayload = { ...payload, print_html_view: printHtml };
            await updateBill(responseData?.id, updateHtmlPayload);
            handlePrint(responseData);
            finishSubmission();
          }, 200);
        } else {
          finishSubmission();
        }

        messageApi.open({
          type: "success",
          content: isedit ? "Bill updated!" : "Bill created!",
        });
        refresh("bills");
      }
    } catch (error) {
      messageApi.error("Submission failed");
    } finally {
      setLoading(false);
    }
  };

  const finishSubmission = () => {
    setBillItems([]);
    onClose();
  };

  const fetchGetFees = async () => {
    try {
      setLoading(true);
      const result = await getFees(searchKeyword, userData?.id);
      if (result.success) {
        const options = result?.data?.map((feeItem) => ({
          label: feeItem.fee_name,
          value: feeItem.id,
          fee_amount: feeItem.fee_amount,
          fee_type: feeItem.fee_type,
        }));
        setGetFeesData(options);
      }
    } catch (err) {
      setGetFeesData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchGetFees();
      if (isedit && editBillData) {
        setPaymentMethod(editBillData?.payment_mode);
        const mappedItems = editBillData?.items.map((item) => ({
          id: item.fee_id,
          fee_name: item.fee_name,
          fee_amount: item.fee_amount,
          fee_type: item.fee_type,
        }));
        setBillItems(mappedItems);
      }
    }
  }, [isOpen, editBillData]);

  const handleAddBillItem = (value, option) => {
    const newItem = {
      id: option.value,
      fee_name: option.label,
      fee_amount: option.fee_amount,
      fee_type: option.fee_type,
    };
    setBillItems((prev) => {
      if (prev.find((item) => item.id === option.value)) return prev;
      return [...prev, newItem];
    });
  };

  const handleRemoveItem = (index) => {
    const newItems = [...billItems];
    newItems.splice(index, 1);
    setBillItems(newItems);
  };

  return (
    <>
      <Modal
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#1890ff", margin: "-20px -24px 20px", padding: "10px 24px", color: "white" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <ArrowLeftOutlined style={{ marginRight: "10px" }} />
              <span>Bill / Payment</span>
            </div>
            <Button type="text" icon={<CloseOutlined />} onClick={onClose} style={{ color: "white" }} />
          </div>
        }
        open={isOpen}
        onCancel={onClose}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
            <Button onClick={() => handleSubmit(false)}>Save & Exit</Button>
            <Button type="primary" onClick={() => handleSubmit(true)}>Save & Print</Button>
          </div>
        }
        width={600}
        closable={false}
      >
        {loading ? <div className="p-5 text-center"><Spin size="large" /></div> : (
          <>
            <h3>Itemized Bill</h3>
            {billItems.map((item, index) => (
              <div key={index} style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                <div>{item.fee_name}</div>
                <div style={{ display: "flex", gap: "10px" }}>
                  <Input type="number" value={item.fee_amount} style={{ width: "120px" }} onChange={(e) => {
                    const newItems = [...billItems];
                    newItems[index].fee_amount = e.target.value;
                    setBillItems(newItems);
                  }} />
                  <Button type="text" danger onClick={() => handleRemoveItem(index)}>✕</Button>
                </div>
              </div>
            ))}
            <Select placeholder="Add bill items" style={{ width: "100%", marginBottom: "20px" }} options={getFeesData} onChange={handleAddBillItem} value={null} />
            
            <h3>Payment</h3>
            <div style={{ display: "flex", gap: "10px" }}>
              <Select value={paymentMethod} onChange={setPaymentMethod} style={{ width: "50%" }} options={[{ value: "CASH", label: "CASH" }, { value: "UPI", label: "UPI" }]} />
              <Input value={`₹${totalBillAmount}`} style={{ width: "50%" }} disabled />
            </div>
          </>
        )}
      </Modal>

      {/* REDESIGNED HIDDEN PRINT TEMPLATE */}
      <div style={{ display: "none" }}>
        <div ref={printRef} style={{ width: "800px" }}>
          <div className="header">
            <h1 className="clinic-name">DR. {userData?.name?.toUpperCase()} {userData?.last_name?.toUpperCase()} CLINIC</h1>
            <p className="clinic-details">{userData?.address}</p>
            <p className="clinic-details">Phone: {userData?.mobile}</p>
          </div>

          <div className="info-section">
            <div className="info-row">
              <div className="info-col"><span className="label">Patient Name:</span><span>{patientData?.first_name} {patientData?.last_name}</span></div>
              <div className="info-col"><span className="label">Contact:</span><span>{patientData?.mobile}</span></div>
            </div>
            <div className="info-row">
              <div className="info-col"><span className="label">Office ID:</span><span>{patientData?.office_id}</span></div>
              <div className="info-col"><span className="label">Bill Date:</span><span>{new Date().toLocaleDateString("en-GB")}</span></div>
            </div>
          </div>

          <div className="bill-meta"><strong>Bill No.:</strong> {billData?.doctor_bill_no}</div>
          <table>
            <thead>
              <tr>
                <th style={{ width: "10%" }}>S. No.</th>
                <th style={{ width: "70%" }}>Items</th>
                <th style={{ width: "20%", textAlign: "right" }}>Price</th>
              </tr>
            </thead>
            <tbody>
              {billItems.map((item, idx) => (
                <tr key={idx}>
                  <td>{idx + 1}</td>
                  <td>{item.fee_name}</td>
                  <td className="text-right">₹{item.fee_amount}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="total-row">Total Amount: ₹{totalBillAmount}</div>

          <div className="bill-meta" style={{ marginTop: "20px" }}><strong>Receipt No.:</strong> {billData?.doctor_receipt_no}</div>
          <table>
            <thead>
              <tr>
                <th style={{ width: "10%" }}>S. No.</th>
                <th style={{ width: "70%" }}>Description</th>
                <th style={{ width: "20%", textAlign: "right" }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>1</td>
                <td>Payment received via {paymentMethod}</td>
                <td className="text-right">₹{totalBillAmount}</td>
              </tr>
            </tbody>
          </table>

          <div className="footer-summary">
            <div>Due: ₹0</div>
            <div>Total Payment: ₹{totalBillAmount}</div>
          </div>
        </div>
      </div>
    </>
  );
};

ItemizedBillModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  patientData: PropTypes.object,
  refresh: PropTypes.func,
  isedit: PropTypes.bool,
  editBillData: PropTypes.object,
};

export default ItemizedBillModal;